import React from 'react';
import {
  FootballSection, PhysicalGpsSection,
  BiometricsSection, AttendanceSection, MentalSection, PdpSection, InjurySection,
} from '../sections';
import { OutsidePeriodBanner } from '../shared';
import FitnessStandardBand from './FitnessStandardBand';
import { MatchAnalysisView, PhysicalGpsAnalysisNotesView, SupportingEvidenceView } from './EvidenceViews';
import DevelopmentTargetBar from '../DevelopmentTargetBar';
import AuthImg from './AuthImg';
import { PerformanceBadge } from '../../PerformanceBadge';
import { SECTION_LABELS, PREVIEW_ORDER, fmtDate, fmtNum } from '../constants';
import './report-document.css';

const ASSESS_LABEL = {
  strength: 'Strength', on_track: 'On Track', developing: 'Developing',
  priority_development_area: 'Priority Development Area', not_assessed: 'Not Assessed',
};
const ASSESS_TONE = {
  strength: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  on_track: 'bg-sky-50 text-sky-700 border-sky-200',
  developing: 'bg-amber-50 text-amber-700 border-amber-200',
  priority_development_area: 'bg-rose-50 text-rose-700 border-rose-200',
  not_assessed: 'bg-slate-100 text-slate-500 border-slate-200',
};

const hasRealTarget = (b) => !!(b && b.status && b.status !== 'insufficient_data');

const Field = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex flex-col sm:flex-row sm:gap-3 py-0.5">
      <dt className="w-44 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap">{value}</dd>
    </div>
  );
};

const SectionTitle = ({ children, testId }) => (
  <h3 className="report-h report-keepwithnext mb-3 pb-1.5 text-[19px] font-bold tracking-tight" data-testid={testId}>{children}</h3>
);

// ── Cover ────────────────────────────────────────────────────────────────
const Cover = ({ overview, report, lead, externalReview = false }) => {
  const o = overview || {};
  const initials = (o.full_name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('');
  const avatar = (
    <div className="flex h-32 w-32 items-center justify-center rounded-2xl bg-white/10 text-5xl font-semibold text-white/70 ring-2 ring-white/20">{initials}</div>
  );
  return (
    <div className="report-cover" data-testid="report-cover">
      <div className="flex items-center gap-3">
        <img src="/pfap-logo-full.png" alt="PFAP" className="h-12 w-auto object-contain"
          onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <span className="text-sm font-semibold tracking-[0.2em] text-[color:var(--pfap-sage)]">PRO FOOTBALL ACADEMY PORTUGAL</span>
      </div>
      {externalReview && report.status === 'draft' && (
        <div className="mt-8 inline-flex border border-amber-300 bg-amber-100 px-3 py-1.5 text-sm font-extrabold text-amber-950" data-testid="external-draft-cover-badge">
          DRAFT — NOT FINAL
        </div>
      )}

      <div className="mt-10">
        <div className="text-xs font-semibold tracking-[0.35em] text-[color:var(--pfap-sage)]">PLAYER DEVELOPMENT REPORT</div>
        <div className="mt-6 flex items-end gap-6">
          <AuthImg src={o.photo_url} alt={o.full_name} className="h-32 w-32 rounded-2xl object-cover ring-2 ring-white/20"
            fallback={avatar} testId="cover-photo" />
          <div>
            <h1 className="text-4xl font-bold leading-tight" data-testid="cover-athlete-name">{o.full_name || report.athlete_name}</h1>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-200">
              {o.position && <span data-testid="cover-position">{o.position}</span>}
              {o.squad && <span>· {o.squad}</span>}
              {o.age_at_report != null && <span>· {o.age_at_report} yrs</span>}
              {o.nationality && <span>· {o.nationality}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-2 gap-x-10 gap-y-3 text-sm sm:grid-cols-3">
        {o.season_name && <CoverMeta label="Season" value={o.season_name} testId="cover-season" />}
        <CoverMeta label="Review Period" value={`${fmtDate(o.review_start_date || report.review_start_date)} – ${fmtDate(o.review_end_date || report.review_end_date)}`} testId="cover-review-period" />
        <CoverMeta label="Report Date" value={fmtDate(o.report_date || report.report_date)} />
        {(o.lead_reviewer || lead) && <CoverMeta label="Lead Reviewer" value={o.lead_reviewer || lead} testId="cover-reviewer" />}
      </div>
    </div>
  );
};
const CoverMeta = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <div className="text-[10px] font-semibold uppercase tracking-widest text-[color:var(--pfap-sage)]">{label}</div>
    <div className="mt-0.5 text-slate-100">{value}</div>
  </div>
);

// ── Development Overview (Summary + Strengths + concise priority list) ─────
const SnapshotChips = ({ chips }) => {
  const list = (chips || []).filter((c) => c && c.value != null && c.value !== '');
  if (list.length === 0) return null;
  return (
    <div className="mb-5 flex flex-wrap gap-2" data-testid="doc-evidence-reviewed">
      {list.map((c, i) => (
        <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-center">
          <div className="text-[15px] font-bold leading-none text-[color:var(--pfap-green)]">{c.value}</div>
          <div className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-400">{c.label}</div>
        </div>
      ))}
    </div>
  );
};

const OverviewBlock = ({ report, snapshot }) => {
  const summary = (report.development_summary || '').trim();
  const strengths = report.strengths || [];
  const priorities = report.priorities || [];
  const hasSnap = (snapshot || []).some((c) => c && c.value != null && c.value !== '');
  if (!summary && strengths.length === 0 && priorities.length === 0 && !hasSnap) return null;
  return (
    <section className="report-section" data-testid="doc-overview">
      <SectionTitle testId="doc-overview-title">Development Overview</SectionTitle>
      <SnapshotChips chips={snapshot} />
      {summary && (
        <div className="mb-5">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Development Summary</div>
          <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-slate-700">{summary}</p>
        </div>
      )}
      {strengths.length > 0 && (
        <div className="mb-5" data-testid="doc-strengths">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Key Strengths</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {strengths.map((s, i) => (
              <div key={s.id || i} className="rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2">
                <div className="text-[13px] font-semibold text-slate-800">{s.title}</div>
                {s.description && <p className="mt-0.5 text-[12px] leading-relaxed text-slate-600">{s.description}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      {priorities.length > 0 && (
        <div data-testid="doc-priority-list">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Priority Development Areas</div>
          <ol className="space-y-1">
            {priorities.map((p, i) => (
              <li key={p.id || i} className="flex items-center gap-2 text-[13px] text-slate-700">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[color:var(--pfap-green)] text-[11px] font-semibold text-white">{i + 1}</span>
                {p.title}
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
};

// ── Fitness (canonical Standards + optional real Development Target) ───────
const FitnessBlock = ({ data }) => {
  const tests = (data && data.tests) || [];
  if (tests.length === 0) return null;
  // Group by category, preserving assembler order.
  const groups = [];
  const byCat = {};
  tests.forEach((t) => {
    const c = t.category || 'Other';
    if (!byCat[c]) { byCat[c] = []; groups.push(c); }
    byCat[c].push(t);
  });
  const outsideDate = (tests.find((t) => t.outside_period) || {}).assessment_date;
  return (
    <div className="space-y-4" data-testid="doc-fitness">
      {data.outside_period && <OutsidePeriodBanner date={fmtDate(outsideDate)} kind="fitness assessment" />}
      {groups.map((cat) => (
        <div key={cat} className="report-section">
          <div className="mb-2 text-[12px] font-bold uppercase tracking-wide text-[color:var(--pfap-green)]">{cat}</div>
          <div className="grid gap-3 sm:grid-cols-2">
            {byCat[cat].map((t, i) => {
              const cur = t.period_latest?.value;
              const prev = t.period_previous?.value;
              const pb = t.personal_best?.value ?? (typeof t.personal_best === 'number' ? t.personal_best : null);
              return (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`doc-fitness-${t.test_type}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-[14px] font-semibold text-slate-800">{t.test_type}</div>
                      <div className="text-[18px] font-bold text-slate-900">
                        {cur != null ? cur : '—'}<span className="ml-1 text-[12px] font-normal text-slate-400">{t.unit}</span>
                      </div>
                    </div>
                    {t.standard?.label && (
                      <PerformanceBadge label={t.standard.label} colorKey={t.standard.color_key} testId={`doc-fitness-std-${t.test_type}`} />
                    )}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-400">
                    Assessed {fmtDate(t.assessment_date)}
                    {prev != null ? ` · Previous ${prev} ${t.unit || ''}` : ''}
                    {pb != null ? ` · PB ${pb} ${t.unit || ''}` : ''}
                  </div>
                  {t.standard_bands
                    ? <FitnessStandardBand bands={t.standard_bands} standard={t.standard} testId={`doc-fitness-band-${t.test_type}`} />
                    : null}
                  {hasRealTarget(t.benchmark) && (
                    <div className="mt-2">
                      <DevelopmentTargetBar comparison={t.benchmark} label="PFAP Development Target" data-testid={`doc-fitness-devtarget-${t.test_type}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Development Action Plan (full priority detail) ─────────────────────────
const ActionPlanBlock = ({ report }) => {
  const priorities = report.priorities || [];
  if (priorities.length === 0) return null;
  return (
    <section className="report-section" data-testid="doc-action-plan">
      <SectionTitle testId="doc-action-plan-title">Development Action Plan</SectionTitle>
      <div className="space-y-4">
        {priorities.map((p, i) => (
          <div key={p.id || i} className="rounded-xl border border-slate-200 bg-white p-4 report-section" data-testid={`doc-action-${i}`}>
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[color:var(--pfap-green)] text-[12px] font-semibold text-white">{i + 1}</span>
              <span className="text-[15px] font-semibold text-slate-800">{p.title}</span>
            </div>
            <dl className="mt-2">
              <Field label="Development Objective" value={p.development_objective} />
              <Field label="Player Actions" value={p.player_action} />
              <Field label="PFAP / Coach Actions" value={p.pfap_action} />
              {!p.player_action && <Field label="Action" value={p.recommended_action} />}
              <Field label="Success Measures" value={p.success_measure} />
              <Field label="Review Date" value={p.review_date ? fmtDate(p.review_date) : null} />
            </dl>
            <SupportingEvidenceView items={p.evidence} testId={`doc-priority-evidence-${i}`} />
          </div>
        ))}
      </div>
    </section>
  );
};

// ── Football Areas + Manual Targets + Staff Assessment (shared sub-renderers) ─
const FootballAreasView = ({ areas }) => (
  <div className="mt-3 space-y-3" data-testid="doc-football-areas">
    {[...areas].sort((a, b) => (a.order || 0) - (b.order || 0)).map((a, i) => {
      const anyBody = a.observation || a.doing_well || a.needs_improvement || a.why_matters || a.expected_standard || a.development_focus;
      if (!a.title && !a.current_assessment && !anyBody) return null;
      return (
        <div key={a.id || i} className="rounded-xl border border-slate-200 bg-white p-3 report-section">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[14px] font-semibold text-slate-800">{a.title || 'Assessment Area'}</span>
            {a.current_assessment && (
              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${ASSESS_TONE[a.current_assessment] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                {ASSESS_LABEL[a.current_assessment] || a.current_assessment}
              </span>
            )}
          </div>
          <dl className="mt-1.5">
            <Field label="What We Observed" value={a.observation} />
            <Field label="Doing Well" value={a.doing_well} />
            <Field label="Needs Improvement" value={a.needs_improvement} />
            <Field label="Why This Matters" value={a.why_matters} />
            <Field label="Expected Standard" value={a.expected_standard} />
            <Field label="Development Focus" value={a.development_focus} />
          </dl>
          <SupportingEvidenceView items={a.evidence} testId={`doc-area-evidence-${i}`} />
        </div>
      );
    })}
  </div>
);

const ManualTargetsView = ({ targets }) => (
  <div className="mt-3" data-testid="doc-manual-targets">
    <div className="mb-1.5 text-[10px] font-medium italic text-slate-400">PFAP Staff Targets — individual development objectives set by PFAP staff; distinct from validated Development Targets and Fitness Standards.</div>
    <div className="grid gap-2 sm:grid-cols-2">
      {[...targets].sort((a, b) => (a.order || 0) - (b.order || 0)).map((t, i) => (
        <div key={t.id || i} className="rounded-lg border border-dashed border-slate-300 bg-slate-50/70 px-3 py-2">
          <div className="text-[13px] font-medium text-slate-800">{t.label || 'Target'}</div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 text-[12px] text-slate-600">
            {t.current_value ? <span>Current <strong>{t.current_value}{t.unit ? ` ${t.unit}` : ''}</strong></span> : null}
            {(t.target_value || t.target_low || t.target_high) ? (
              <span>Target <strong>{t.target_value || `${t.target_low ?? ''}–${t.target_high ?? ''}`}{t.unit ? ` ${t.unit}` : ''}</strong></span>
            ) : null}
            {t.context ? <span className="text-slate-500">Focus: {t.context}</span> : null}
          </div>
          {t.explanation && <div className="mt-0.5 text-[11px] text-slate-500">{t.explanation}</div>}
        </div>
      ))}
    </div>
  </div>
);

const StaffAssessmentView = ({ text }) => (
  <div className="mt-3 rounded-lg border-l-4 border-[color:var(--pfap-sage)] bg-slate-50 px-3 py-2.5" data-testid="doc-staff-assessment">
    {String(text).split(/\n{2,}/).map((p, i) => (
      <p key={i} className="text-[13px] leading-relaxed text-slate-700 [&:not(:first-child)]:mt-2 whitespace-pre-wrap">{p}</p>
    ))}
  </div>
);

const HUB = {
  football_performance: (s) => <FootballSection data={s.data} showMatches={false} />,
  physical_gps: (s) => <PhysicalGpsSection data={s.data} showMatches={false} />,
  fitness: (s) => <FitnessBlock data={s.data} />,
  biometrics: (s) => <BiometricsSection data={s.data} />,
  attendance: (s) => <AttendanceSection data={s.data} />,
  mental: (s) => <MentalSection data={s.data} />,
  pdp: (s) => <PdpSection data={s.data} />,
  injury_rehab: (s) => <InjurySection data={s.data} />,
};

const ClosingBlock = ({ report, lead }) => {
  const focus = (report.primary_focus || '').trim();
  const msg = (report.key_message || '').trim();
  const reviewDate = report.next_review_date;
  if (!focus && !msg && !reviewDate) return null;
  return (
    <section className="report-section" data-testid="doc-closing">
      <SectionTitle testId="doc-closing-title">Next Review Period</SectionTitle>
      {focus && (
        <div className="mb-3">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Primary Development Focus</div>
          <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-slate-700">{focus}</p>
        </div>
      )}
      {msg && (
        <div className="rounded-xl border-l-4 border-[color:var(--pfap-accent)] bg-amber-50/60 px-4 py-3" data-testid="doc-key-message">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--pfap-accent)]">Key Message for the Player</div>
          <p className="whitespace-pre-wrap text-[14px] font-medium leading-relaxed text-slate-800">{msg}</p>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-x-8 gap-y-1 text-[12px] text-slate-500">
        {reviewDate && <span>Next Review: <strong className="text-slate-700">{fmtDate(reviewDate)}</strong></span>}
        {(report.snapshot?.lead_reviewer || lead) && <span>Lead Reviewer: <strong className="text-slate-700">{report.snapshot?.lead_reviewer || lead}</strong></span>}
      </div>
    </section>
  );
};

function computeSnapshot(sections) {
  const fb = sections?.football_performance?.data;
  const ma = sections?.match_analysis?.data;
  const gps = sections?.physical_gps?.data;
  const fit = sections?.fitness?.data;
  const att = sections?.attendance?.data;
  const attRate = att?.attendance_rate ?? att?.rate ?? att?.attendance_percentage;
  return [
    { label: 'Matches', value: fb?.matches != null ? fb.matches : (fb?.appearances != null ? fb.appearances : null) },
    { label: 'Minutes', value: fb?.minutes ?? null },
    { label: 'Video Observations', value: ma?.count ?? null },
    { label: 'GPS Matches', value: gps?.sessions ?? null },
    { label: 'Fitness Tests', value: (fit?.tests && fit.tests.length) ? fit.tests.length : null },
    { label: 'Attendance', value: attRate != null ? `${attRate}%` : null },
  ];
}

/**
 * ReportDocument — THE single professional presentation used by the Final
 * Document Preview, the finalized web report, the /print route and the PDF.
 * Consumes the normalized { report, sections, overview, lead } model.
 */
const ReportDocument = ({ report, sections, overview, lead = null, printMode = false, externalReview = false }) => {
  const selected = new Set(report.selected_sections || []);
  const includedIds = new Set((report.selected_evidence || []).map((e) => e.source_id));
  const dataOrder = PREVIEW_ORDER.filter((k) => !['overview', 'development_summary'].includes(k));

  const renderDataSection = (k) => {
    if (!selected.has(k)) return null;
    const s = sections?.[k];
    const cfg = { include_in_report: true, show_hub_data: true, show_staff_assessment: true, ...((report.section_config || {})[k] || {}) };
    if (!cfg.include_in_report) return null;
    const hasHubRaw = !!(s && s.allowed !== false && s.has_data && cfg.show_hub_data);
    const maIncluded = (k === 'match_analysis' && hasHubRaw)
      ? (s.data.observations || []).filter((o) => includedIds.has(o.id)).length
      : null;
    const hasHub = k === 'match_analysis' ? (hasHubRaw && maIncluded > 0) : hasHubRaw;
    const assessment = cfg.show_staff_assessment ? (report.section_summaries?.[k] || report.staff_assessments?.[k] || '').trim() : '';
    const areas = (k === 'football_performance' && cfg.show_staff_assessment) ? (report.football_areas || []) : [];
    const areasHaveContent = areas.some((a) => a.title || a.current_assessment || a.observation || a.doing_well || a.needs_improvement || a.why_matters || a.expected_standard || a.development_focus || (a.evidence || []).length);
    const targets = (report.manual_targets || []).filter((t) => t.section_key === k);
    const sectionEvidence = (report.section_evidence || {})[k] || [];
    const gpsNotes = k === 'physical_gps' ? (report.physical_gps_analysis_notes || []) : [];
    if (!hasHub && !assessment && !areasHaveContent && targets.length === 0 && sectionEvidence.length === 0 && gpsNotes.length === 0) return null;

    const hub = hasHub ? (k === 'match_analysis'
      ? <MatchAnalysisView data={s.data} includedIds={includedIds} />
      : (HUB[k] ? HUB[k](s) : null)) : null;


    // Football uses two labels: manual "Football Development" + hub "Match Performance".
    const label = SECTION_LABELS[k];
    return (
      <section key={k} className="report-section" data-testid={`doc-section-${k}`}>
        <SectionTitle testId={`doc-section-${k}-title`}>{label}</SectionTitle>
        {k === 'football_performance' && areasHaveContent && (
          <>
            <div className="mb-1 text-[12px] font-bold uppercase tracking-wide text-[color:var(--pfap-green)]">Football Development</div>
            <FootballAreasView areas={areas} />
          </>
        )}
        {hub && (
          <div className={k === 'football_performance' && areasHaveContent ? 'mt-4' : ''}>
            {k === 'football_performance' && <div className="mb-1 text-[12px] font-bold uppercase tracking-wide text-[color:var(--pfap-green)]">Match Performance</div>}
            {hub}
          </div>
        )}
        {targets.length > 0 && <ManualTargetsView targets={targets} />}
        {sectionEvidence.length > 0 && <SupportingEvidenceView items={sectionEvidence} testId={`doc-section-evidence-${k}`} />}
        {gpsNotes.length > 0 && <PhysicalGpsAnalysisNotesView notes={gpsNotes} evidence={sectionEvidence.filter((item) => item?.include_in_final !== false)} />}
        {assessment && <StaffAssessmentView text={assessment} />}
      </section>
    );
  };

  const customs = (report.custom_sections || [])
    .filter((c) => c.include_in_report !== false && (c.title || c.body))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const isDraft = report.status === 'draft';
  const snapshot = computeSnapshot(sections);

  return (
    <div className={`report-doc ${printMode ? 'report-doc-print' : ''}`} data-testid="report-document">
      {isDraft && <div className="report-draft-watermark" data-testid="doc-draft-watermark">DRAFT — NOT FINAL</div>}
      <div className="report-paper">
        <Cover overview={overview} report={report} lead={lead} externalReview={externalReview} />
        <div className="report-inner space-y-7">
          {externalReview && isDraft && (
            <div className="border-l-4 border-amber-500 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900 report-section" data-testid="external-draft-notice">
              This report is currently under review and may still be updated before finalization.
            </div>
          )}
          <OverviewBlock report={report} snapshot={snapshot} />
          {dataOrder.map((k) => renderDataSection(k))}
          <ActionPlanBlock report={report} />
          {customs.length > 0 && customs.map((c, i) => (
            <section key={c.id || i} className="report-section" data-testid={`doc-custom-${i}`}>
              <SectionTitle>{c.title || 'Additional Notes'}</SectionTitle>
              {c.body && <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-slate-700">{c.body}</p>}
            </section>
          ))}
          <ClosingBlock report={report} lead={lead} />
          <div className="report-footer border-t border-slate-200 pt-3 text-center text-[10px] text-slate-400">
            Pro Football Academy Portugal · Player Development Report · Confidential
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDocument;
